// Shared pagination primitives — UI_Behavior §4.10 L661-667. Originally the
// catalogue-only DealsPage.pagination.ts module; promoted here when the
// back-office tables gained the same contract (ADR-056) so neither side
// imports from the other's route folder. Pure module: unit-tests without
// mounting React or the Supabase-backed hooks.

export type PaginationItem = number | 'ellipsis';

// Up to 7 page numbers render plainly; beyond that the list collapses to
// exactly 7 slots: first + last always visible, a 3-wide window around the
// current page, ellipsis filling the gap on each overflowing side.
const MAX_PLAIN_PAGES = 7;

/** Both `currentIdx` and the returned page numbers are 0-based. */
export function buildPaginationItems(currentIdx: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 0) return [];
  if (totalPages <= MAX_PLAIN_PAGES) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const last = totalPages - 1;
  // Near an edge the window slides instead of shrinking, so the list keeps
  // a stable 7-slot width while the current page always shows a neighbour
  // on each side.
  if (currentIdx <= 3) return [0, 1, 2, 3, 4, 'ellipsis', last];
  if (currentIdx >= last - 3) {
    return [0, 'ellipsis', last - 4, last - 3, last - 2, last - 1, last];
  }
  return [0, 'ellipsis', currentIdx - 1, currentIdx, currentIdx + 1, 'ellipsis', last];
}

/** Admin rows-per-page contract — UI_Behavior §4.10 L663-665. */
export const ADMIN_PAGE_SIZES = [20, 50, 100] as const;
export const ADMIN_DEFAULT_PAGE_SIZE = 50;

/**
 * Normalize a raw `?page=` URL value (1-based) to a 0-based index WITHOUT
 * knowing the total yet — the count rides the first server response, so
 * clamping to the last real page is a separate post-result step
 * (`clampPage`). Anything that isn't an integer ≥ 1 (`abc`, `0`, `-3`,
 * `1.5`) falls back to the first page.
 */
export function parsePageParam(raw: string | null): number {
  if (raw === null) return 0;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return 0;
  return n - 1;
}

/** A `?pageSize=` outside the 20/50/100 contract falls back to 50. */
export function parsePageSizeParam(raw: string | null): number {
  if (raw === null) return ADMIN_DEFAULT_PAGE_SIZE;
  const n = Number(raw);
  return (ADMIN_PAGE_SIZES as readonly number[]).includes(n) ? n : ADMIN_DEFAULT_PAGE_SIZE;
}

/**
 * Last valid 0-based page index for a counted result set — 0 when empty.
 * Used after a successful fetch to fold a stale deep link (or a result set
 * that shrank under the current offset) back onto a real page.
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  const lastIdx = Math.max(0, Math.ceil(total / pageSize) - 1);
  return Math.min(Math.max(0, page), lastIdx);
}
